import { api, type Api } from "@api/bot-base-api-request";
import { useMutation } from "@tanstack/react-query";

import {
  NotificationParams,
  useNotification,
} from "@/providers/notifications-bot-actions/notifications-bot-actions";

const BOT_BUY_TOKEN_QUERY_KEY = "bot-buy-token";

type BotBuyTokenParams = {
  appPubKey: string;
  userId: string;
  chainId: number;
  tokenAddress: string;
  walletAddress: string;
  amount: string;
  slippage?: number;
  isAutoSellOn: boolean;
};

// extended for notification data
type BotBuyTokenInput = {
  params: BotBuyTokenParams;
  notifyParams: NotificationParams;
};

type BotBuyTokenResponse = {
  txHash: string | null;
  isSuccess: boolean;
  errMessage?: unknown;
};

const postBotBuyToken = async (
  api: Api,
  params: BotBuyTokenParams
): Promise<BotBuyTokenResponse> => {
  return api.post("trade/buy", { json: params }).json();
};

export function useBotBuyTokenMutation() {
  const { setNotifyProps } = useNotification();

  return useMutation<BotBuyTokenResponse, Error, BotBuyTokenInput>({
    mutationFn: async (input: BotBuyTokenInput) =>
      postBotBuyToken(api, input.params),
    mutationKey: [BOT_BUY_TOKEN_QUERY_KEY],
    onSuccess: async (_data, variables) => {
      const params = inputToNotificationParams(variables);

      if (_data.isSuccess) {
        setNotifyProps({
          type: "success",
          action: "buy",
          params,
        });
      } else {
        setNotifyProps({
          type: "error",
          action: "buy",
          params,
        });
      }
    },
    onError: async (_error, variables) => {
      setNotifyProps({
        type: "error",
        action: "buy",
        params: inputToNotificationParams(variables),
      });
    },
  });
}

const inputToNotificationParams = (
  inputParams: BotBuyTokenInput
): NotificationParams => {
  const params: NotificationParams = {
    youReceive: inputParams.notifyParams.youReceive,
    tokenSymbol: inputParams.notifyParams.tokenSymbol,
    youPayNative: inputParams.notifyParams.youPayNative,
    chainSymbol: inputParams.notifyParams.chainSymbol,
  };
  return params;
};

export { postBotBuyToken, BOT_BUY_TOKEN_QUERY_KEY };
